const DIGIT_MAP = {
  "٠": "0",
  "١": "1",
  "٢": "2",
  "٣": "3",
  "٤": "4",
  "٥": "5",
  "٦": "6",
  "٧": "7",
  "٨": "8",
  "٩": "9",
  "۰": "0",
  "۱": "1",
  "۲": "2",
  "۳": "3",
  "۴": "4",
  "۵": "5",
  "۶": "6",
  "۷": "7",
  "۸": "8",
  "۹": "9",
};

const ARABIC_DIGIT_RE = /[٠-٩۰-۹]/g;
const HAS_ARABIC_DIGIT_RE = /[٠-٩۰-۹]/;
const DIGIT_ONLY_ATTRS = ["value", "placeholder", "title", "aria-label"];

export const toLatinDigits = (value) =>
  String(value ?? "").replace(
    ARABIC_DIGIT_RE,
    (digit) => DIGIT_MAP[digit] || digit,
  );

export const formatLatinNumber = (value, options = {}) => {
  const numericValue = Number(value ?? 0);
  return toLatinDigits(
    new Intl.NumberFormat("en-US", {
      maximumFractionDigits: 2,
      ...options,
    }).format(Number.isFinite(numericValue) ? numericValue : 0),
  );
};

export const formatLatinMoney = (value, options = {}) =>
  formatLatinNumber(value, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...options,
  });

export const formatLatinDate = (value) => {
  if (!value) return "—";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return toLatinDigits(value);
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return toLatinDigits(value);
  return toLatinDigits(
    new Intl.DateTimeFormat("en-CA", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(date),
  );
};

const withLatinNumbering = (locales) => {
  if (typeof locales === "string") {
    if (locales.includes("-u-") && !locales.includes("nu-")) {
      return `${locales}-nu-latn`;
    }
    return locales.startsWith("ar") && !locales.includes("nu-latn")
      ? `${locales}${locales.includes("-u-") ? "-nu-latn" : "-u-nu-latn"}`
      : locales;
  }
  if (Array.isArray(locales)) return locales.map(withLatinNumbering);
  return locales;
};

const wrapLocaleMethod = (prototype, methodName) => {
  const original = prototype?.[methodName];
  if (typeof original !== "function" || original.__tajerLatinDigits) return;
  const wrapped = function patchedLocaleMethod(locales, options) {
    return toLatinDigits(
      original.call(this, withLatinNumbering(locales), {
        numberingSystem: "latn",
        ...options,
      }),
    );
  };
  wrapped.__tajerLatinDigits = true;
  prototype[methodName] = wrapped;
};

const normalizeElementDigits = (element) => {
  if (!element || element.nodeType !== 1) return;

  DIGIT_ONLY_ATTRS.forEach((attr) => {
    if (!element.hasAttribute?.(attr)) return;
    const current = element.getAttribute(attr);
    const next = toLatinDigits(current);
    if (current !== next) element.setAttribute(attr, next);
  });

  if (
    "value" in element &&
    typeof element.value === "string" &&
    HAS_ARABIC_DIGIT_RE.test(element.value)
  ) {
    element.value = toLatinDigits(element.value);
  }
};

const normalizeNodeDigits = (node) => {
  if (!node) return;
  if (node.nodeType === Node.TEXT_NODE) {
    const next = toLatinDigits(node.nodeValue);
    if (node.nodeValue !== next) node.nodeValue = next;
    return;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return;
  normalizeElementDigits(node);
  node.querySelectorAll?.("*").forEach(normalizeElementDigits);
  const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
  let textNode = walker.nextNode();
  while (textNode) {
    const next = toLatinDigits(textNode.nodeValue);
    if (textNode.nodeValue !== next) textNode.nodeValue = next;
    textNode = walker.nextNode();
  }
};

const installDomDigitNormalizer = () => {
  if (typeof window === "undefined" || typeof MutationObserver === "undefined") return;
  if (window.__tajerLatinDigitObserver) return;

  const normalizeDocument = () => normalizeNodeDigits(document.body);
  if (document.body) normalizeDocument();
  else document.addEventListener("DOMContentLoaded", normalizeDocument, { once: true });

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === "characterData") {
        normalizeNodeDigits(mutation.target);
      } else if (mutation.type === "attributes") {
        normalizeElementDigits(mutation.target);
      } else {
        mutation.addedNodes.forEach(normalizeNodeDigits);
      }
    });
  });

  const start = () => {
    if (!document.body) return;
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: DIGIT_ONLY_ATTRS,
    });
    window.__tajerLatinDigitObserver = observer;
  };

  if (document.body) start();
  else document.addEventListener("DOMContentLoaded", start, { once: true });
};

export const installLatinNumerals = () => {
  if (
    typeof Intl !== "undefined" &&
    Intl.NumberFormat &&
    !Intl.NumberFormat.__tajerLatinDigits
  ) {
    const OriginalNumberFormat = Intl.NumberFormat;
    const PatchedNumberFormat = function NumberFormat(locales, options = {}) {
      return new OriginalNumberFormat(withLatinNumbering(locales), {
        numberingSystem: "latn",
        ...options,
      });
    };
    Object.setPrototypeOf(PatchedNumberFormat, OriginalNumberFormat);
    PatchedNumberFormat.prototype = OriginalNumberFormat.prototype;
    PatchedNumberFormat.__tajerLatinDigits = true;
    Intl.NumberFormat = PatchedNumberFormat;
  }

  wrapLocaleMethod(Number.prototype, "toLocaleString");
  wrapLocaleMethod(Date.prototype, "toLocaleString");
  wrapLocaleMethod(Date.prototype, "toLocaleDateString");
  wrapLocaleMethod(Date.prototype, "toLocaleTimeString");
  installDomDigitNormalizer();
};
